import { Component, h } from 'preact'
import InputGroup from '../ui-components/InputGroup'
import axios from 'axios'
import TimezoneSelector, { TimezoneList } from './TimezoneSelector'
import AlertComponent from '../ui-components/AlertComponent'
import Header from '../header'
import Switcher from '../control/Switcher'
import classNames from 'classnames'
import { Section } from './Section'
import ButtonControl from '../button-control'
import SimpleFilterSelector from '../overview/filters/SimpleFilterSelector'
import Icon from '../assets'
import { normalizeRoles } from './utils'
import { Activity } from './interfaces'

type configItem = {
  activityName: string,
  points: number,
  enabled?: boolean
}

interface State {
  activityList: Array<Activity>,
  selectedTimezone: string,
  timezoneList: TimezoneList,
  isSaveButtonLoading: boolean,
  messageType?: string|null,
  message?: string,
  isUserRoleDropdownOpen: boolean,
  isUserRolesLoading: boolean,
  userRoles: Array<any>,
  selectedRoles: Array<string>,
  timezoneSelectionError: boolean
}

interface Props {
    csrfToken: string,
    destinationUrl: string
}

class PointSystemConfiguration extends Component<Props, State> {
  configurableActivityList: Array<Activity> = [
    {
      activityName: 'provided answer',
      points: 0
    },
    {
      activityName: 'replied topic',
      points: 0
    },
    {
      activityName: 'received like',
      points: 0
    },
    {
      activityName: 'given like',
      points: 0
    },
    {
      activityName: 'created topic',
      points: 0
    }]

  constructor (props: Props) {
    super(props)
    this.state = {
      activityList: [],
      selectedTimezone: '',
      timezoneList: [],
      messageType: null,
      message: '',
      isSaveButtonLoading: false,
      isUserRoleDropdownOpen: false,
      isUserRolesLoading: false,
      userRoles: [],
      selectedRoles: [],
      timezoneSelectionError: false
    }
    this.onTimezoneSelected = this.onTimezoneSelected.bind(this)
    this.combineActivityList = this.combineActivityList.bind(this)
    this.onUserRolesChanged = this.onUserRolesChanged.bind(this)
    this.onResetUserRoles = this.onResetUserRoles.bind(this)
    this.fetchConfiguration = this.fetchConfiguration.bind(this)
    this.fetchUserRoles = this.fetchUserRoles.bind(this)
  }

  onTimezoneSelected (timezoneValue: string) {
    this.setState({ timezoneSelectionError: false, selectedTimezone: timezoneValue })
  }

  combineActivityList (configuration: Array<configItem>, customerConfiguration: Array<configItem>): Array<configItem> {
    let defaultConfigurationObject: {
      [key: string]: {
        points: number,
        enabled: boolean
      }
    } = {}
    configuration.forEach((configurationItem: configItem) => {
      defaultConfigurationObject[configurationItem.activityName] = {
        points: configurationItem.points,
        enabled: !!configurationItem.enabled
      }
    })

    let customerConfigurationObject: {
      [key: string]: {
        points: number,
        enabled: boolean
      }
    } = {}
    customerConfiguration.forEach((configurationItem: configItem) => {
      customerConfigurationObject[configurationItem.activityName] = {
        points: configurationItem.points,
        enabled: !!configurationItem.enabled
      }
    })

    const combinedConfigurationObject = Object.assign({}, defaultConfigurationObject, customerConfigurationObject)
    return Object.keys(combinedConfigurationObject).map(combinedConfigurationItem => ({
      activityName: combinedConfigurationItem,
      points: combinedConfigurationObject[combinedConfigurationItem].points,
      enabled: combinedConfigurationObject[combinedConfigurationItem].enabled
    }))
  }

  async componentDidMount () {
    this.setState({ messageType: null, isUserRolesLoading: true })
    this.fetchConfiguration()
    this.fetchUserRoles()
  }

  async fetchConfiguration () {
    try {
      const res = await axios.get('/settings/pointSystem/configuration')

      let configuration = this.configurableActivityList
      if (res.data) {
        if (res.data.activityList) {
          configuration = this.combineActivityList(this.configurableActivityList, res.data.activityList)
        }
        if (res.data.timezone) {
          this.setState({ selectedTimezone: res.data.timezone })
        }
        if (res.data.timezoneList) {
          this.setState({ timezoneList: res.data.timezoneList })
        }
        if (res.data.excludedUserRoles) {
          this.setState({ selectedRoles: res.data.excludedUserRoles })
        }
      }

      this.setState({ activityList: configuration, isUserRolesLoading: false })
    } catch (e) {
      this.setState({ messageType: 'error', message: 'Unable to retrieve point system configuration.', isUserRolesLoading: false })
    }
  }

  async fetchUserRoles () {
    try {
      const res = await axios.get('/role/fetchRoles')
      const roles = normalizeRoles(res.data)
      this.setState({ userRoles: roles, isUserRolesLoading: false })
    } finally {
      this.setState({ isUserRolesLoading: false })
    }
  }

  async onSave () {
    this.setState({ messageType: '' })
    if (!this.validate()) {
      return
    }
    if (this.state.selectedTimezone === '') {
      this.setState({ timezoneSelectionError: true })
      return
    }
    this.setState({ isSaveButtonLoading: true })
    let dataParameters = {
      timezone: this.state.selectedTimezone,
      activityList: this.state.activityList,
      excludedUserRoles: this.state.selectedRoles
    }

    try {
      await axios.post('/settings/pointSystem/configuration', dataParameters)
      this.setState({
        messageType: 'success',
        message: 'Changes saved successfully.'
      })
    } catch (e) {
      this.setState({
        messageType: 'error',
        message: 'Failed to save configuration'
      })
    }
    this.setState({ isSaveButtonLoading: false })
  }

  validate () {
    const activityList = this.state.activityList.map(activity => Object.assign(activity, {
      error: (activity.enabled && activity.points < 1)
        ? 'Please enter a number greater than 0 to enable this rule'
        : undefined
    }))
    this.setState({ activityList: activityList })
    return typeof activityList.find(activity => activity.error) === 'undefined'
  }

  onPointsChange (points: number, i: number) {
    let activityList = this.state.activityList
    activityList[i]['points'] = Math.max(points, 0)
    activityList[i]['error'] = undefined
    this.setState({ activityList: activityList })
  }

  onActivityToggle (activity: Activity, i: number) {
    let activityList = this.state.activityList
    if (!activity.enabled && activity.points < 1) {
      activityList[i]['error'] = 'Please enter a number greater than 0 to enable this rule'
    } else {
      activityList[i]['enabled'] = !activity.enabled
      activityList[i]['error'] = undefined
    }
    this.setState({ activityList: activityList })
  }

  onUserRolesChanged (data: Array<string>) {
    this.setState({
      isUserRoleDropdownOpen: false,
      selectedRoles: data
    })
  }

  onResetUserRoles () {
    this.setState({
      isUserRoleDropdownOpen: false
    })
  }

  render (props: Props, { messageType, message, isSaveButtonLoading, activityList, timezoneList, selectedTimezone, isUserRoleDropdownOpen, isUserRolesLoading, userRoles, selectedRoles, timezoneSelectionError }: State) {
    const isVisibleFor = messageType === 'success' ? 5000 : false
    const selectedUserRolesLength = selectedRoles?.length
    const userRolesPlaceholderText = selectedUserRolesLength === 0 ? 'Select user roles to exclude' : (selectedUserRolesLength === 1 ? `${selectedUserRolesLength} user role selected` : `${selectedUserRolesLength} user roles selected`)
    return (
      <div className='point-system-container'>
        <Header title='Point system' saveCallback={() => this.onSave()} disableSaveButton={false} isLoading={isSaveButtonLoading} />
        <Section title='Point system rules' customClass='point-system-rules'>
          {
            messageType ? <AlertComponent messageType={messageType} message={message} sticky renderHTML isVisibleFor={isVisibleFor} /> : null
          }
          {activityList.map((activity, i) => {
            const activityName = ['received like', 'given like'].includes(activity.activityName) ? activity.activityName.concat(' or vote') : activity.activityName
            return (<div class={classNames({
              'activity-row': true,
              'validation-error': activity.error
            })}>
              <Switcher customClass='section-switch' on={activity.enabled || false} onChange={() => {
                this.onActivityToggle(activity, i)
              }} />
              <span class='activity-description-left'>Award </span>
              <InputGroup key={activity.activityName} error={activity.error} type='number' min={0} required
                qaClasses='qa-points' placeholder='0' value={activity.points}
                onChangeCallback={(e: ChangeEvent) => this.onPointsChange(+e.target.value, i)} />
              <span class='activity-description-right'>points for {activityName}</span>
            </div>)
          })}
        </Section>
        <Section title='Timezone setting' customClass='point-system-timezone-container'>
          <div className='point-system-timezone-description'>
            The weekly user leaderboard automatically resets at midnight on Sunday/Monday according to your timezone selection.
          </div>
          <div className='timezone-selector-container'>
            <label className='timezone-selector-label'>Timezone</label>
            <TimezoneSelector
              timezones={timezoneList}
              selected={timezoneList.find((timezone) => timezone.value === selectedTimezone)}
              onChange={this.onTimezoneSelected}
              error={timezoneSelectionError}
            />
          </div>
        </Section>
        <Section title='Leaderboard page setting' customClass='point-system-leaderboard-page-setting'>
          <div className='point-system-leaderboard-page-setting-description'>
            Please note that this setting only applies to <a href={`${props.destinationUrl}/leaderboard`} target='_blank' className='link'>leaderboard page</a>, leaderboard widget is not affected.
          </div>
          <div className='user-role-selector'>
            <label className='user-role-selector-label'>Select user roles to exclude from leaderboard page</label>
            <div> <ButtonControl disabled={isUserRolesLoading} newStyle title={<div>
              <span className='dropdown-placeholder-text'> {userRolesPlaceholderText}</span>
              <Icon customClass='dropdown-chevron-down-icon' icon='chevronDown' height={16} width={16} />
            </div>} size='medium' class={`user-roles-open-button user-role-selector-button ${isUserRoleDropdownOpen ? 'dropdown-open' : ''} `}
              onClick={() => this.setState({ isUserRoleDropdownOpen: !isUserRoleDropdownOpen })} /> </div>
            {isUserRoleDropdownOpen &&
            <div className='overview__filters-box-container'>
              <div className='overview__filters-box'>
                <SimpleFilterSelector id='userRole' name='user role ' isSearchable items={userRoles} selectedFilterItems={selectedRoles}>
                  {({ isApplyButtonDisabled, data }: { isApplyButtonDisabled: boolean, data: any }) =>
                    <div>
                      <hr />
                      <div className='overview__filters-box-buttons'>
                        <ButtonControl
                          newStyle
                          size='small'
                          variant='brand'
                          title='Apply'
                          disabled={false}
                          onClick={() => this.onUserRolesChanged(data)} />
                        <ButtonControl newStyle size='small' title='Cancel' onClick={this.onResetUserRoles} />
                      </div>
                    </div>
                  }
                </SimpleFilterSelector>
              </div>
            </div>}
          </div>
        </Section>
      </div>
    )
  }
}

export default PointSystemConfiguration
